import { useState } from 'react';
import { Download, FileText, Presentation } from 'lucide-react';
import type { TrainingExportFormat } from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover.js';
import { postJob, useJobRunner } from './useTraining.js';

/**
 * Export one session's slides, or the whole training (every session with slides), as PDF or
 * PowerPoint. The file is rendered server-side by the export service (headless Chromium /
 * pptxgenjs) as a job; once it's stored, a signed URL downloads it.
 */
export function TrainingExportMenu({
  projectId,
  sessionId,
  disabled,
  label = 'Export',
}: {
  projectId: string;
  sessionId?: string;
  disabled?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const { start, running, error } = useJobRunner(async (result) => {
    const exportId = result?.exportId as string | undefined;
    if (!exportId) return;
    const { downloadUrl } = await api.get<{ downloadUrl: string }>(
      `/projects/${projectId}/training/exports/${exportId}/download-url`,
    );
    window.location.assign(downloadUrl);
  });

  function exportAs(format: TrainingExportFormat) {
    setOpen(false);
    void start(
      () => postJob(projectId, '/exports', { format, ...(sessionId ? { sessionId } : {}) }),
      'Could not start the export.',
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button disabled={disabled || running}>
            <Download />
            {running ? 'Exporting…' : label}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-48 flex-col p-1">
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm hover:bg-surface-2 md:py-1.5"
            onClick={() => exportAs('pdf')}
          >
            <FileText className="h-4 w-4" /> PDF
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm hover:bg-surface-2 md:py-1.5"
            onClick={() => exportAs('pptx')}
          >
            <Presentation className="h-4 w-4" /> PowerPoint (.pptx)
          </button>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
